import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { NumberFormatter } from '@repo/toolkit/number'
import { FileTypeEnum, IFile } from '@repo/toolkit/types'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import { DetailedList, DetailedListDetail } from '@repo/ui/detailed-list'
import { ClipboardIcon, MinusIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import useTranslate from '../../../lib/hooks/use-translate'
import { TravelExpensesStatusEnum } from '../../../lib/types/enums/affected-staff/affected-staff-status.enum'
import { centerStatusMapping } from '../../../lib/types/enums/center/center-status'
import { CurrencyEnum } from '../../../lib/types/enums/common/currency.enum'
import { TravelExpenseTypeEnum } from '../../../lib/types/enums/travel-expense/travel-expense.enum'
import { RoleEnum } from '../../../lib/types/enums/users/role.enum'
import {
  TravelExpense,
  TravelExpenseInput
} from '../../../lib/types/models/travel-expenses/travel-expenses'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { isEmptyRow } from '../../../views/staff-personal-space/staff-home/utils'
import { EditableTableColumn } from '../../elements/editable-table'
import {
  InvoiceButton,
  UploadInvoicesButton,
  userInvoiceColorMapping,
  userUploadInvoiceColorMapping
} from '../centers/center-costs/center.ui'
import { TravelExpensesTable } from '../staff/staff-personal-space/travel-expenses.ui'
import {
  getTravelExpensesColumns,
  mapTravelExpensesInputToRequest,
  mapTravelExpensesToInput,
  TravelExpenseApiRequest
} from '../staff/staff-personal-space/travel-expenses.utils'
import TravelExpensesCurrencyInput from './travel-expenses-currency-input'

type Props = {
  id: number
  mode: 'admin' | 'staff'
  travelExpensesCurrency: CurrencyEnum
  travelExpensesStatus: TravelExpensesStatusEnum
  travelExpensesSentAt?: Date
  // The 170 € flat fee cap does not apply to UK staff
  isForeignStaff?: boolean
  getTravelExpenses: (id: number) => Promise<TravelExpense[]>
  updateTravelExpensesCurrency: (currency: CurrencyEnum) => void
  createOrUpdateTravelExpenses: (
    id: number,
    input: TravelExpenseApiRequest[]
  ) => Promise<TravelExpense[]>
  sendTravelExpensesToLec: (
    id: number,
    input: TravelExpenseApiRequest[]
  ) => Promise<TravelExpense[]>
  openTravelExpenses?: (id: number) => Promise<void>
  validateTravelExpenses?: (id: number, input: TravelExpenseApiRequest[]) => Promise<void>

  onSuccess: () => Promise<void>
  onOpen?: () => Promise<void>
}
const TravelExpenses: React.FC<Props> = ({
  id,
  mode,
  travelExpensesCurrency,
  travelExpensesStatus,
  travelExpensesSentAt,
  isForeignStaff = false,
  getTravelExpenses,
  updateTravelExpensesCurrency,
  createOrUpdateTravelExpenses,
  sendTravelExpensesToLec,
  openTravelExpenses,
  validateTravelExpenses,
  onSuccess,
  onOpen
}) => {
  const { t } = useTranslate()

  const [currency, setCurrency] = useState(travelExpensesCurrency)
  const isEditable = travelExpensesStatus === TravelExpensesStatusEnum.PENDING
  const [newTravelExpenses, setNewTravelExpenses] = useState<TravelExpenseInput[]>([])
  const nextTempId = useRef(-1)

  const { refetch: refetchTravelExpenses } = useQuery({
    queryKey: [id, 'travel-expenses'],
    queryFn: async () => {
      const res = await getTravelExpenses(id)
      setNewTravelExpenses(res.map(mapTravelExpensesToInput))
      return res
    },
    enabled: !!id
  })

  const saveTravelExpenses = useMutation({
    mutationFn: (input: TravelExpenseInput[]) => {
      return createOrUpdateTravelExpenses(id, input.map(mapTravelExpensesInputToRequest))
    },
    onError: showAxiosError,
    onSuccess: () => {
      toast.success(t('staff.personal.space.my.journeys.details.expenses.submission.success'))
      refetchTravelExpenses()
    }
  })

  const validateTravelExpensesMutation = useMutation({
    mutationFn: async (input: TravelExpenseInput[]) => {
      await validateTravelExpenses?.(id, input.map(mapTravelExpensesInputToRequest))
      await onSuccess()
    },
    onError: showAxiosError,
    onSuccess: () => {
      toast.success(t('travel.expenses.submission.success'))
      refetchTravelExpenses()
    }
  })

  const sendTravelExpensesToLecMutation = useMutation({
    mutationFn: (input: TravelExpenseInput[]) => {
      return sendTravelExpensesToLec(id, input.map(mapTravelExpensesInputToRequest))
    },
    onError: showAxiosError,
    onSuccess: () => {
      toast.success(t('staff.personal.space.my.journeys.details.expenses.submission.success'))
      refetchTravelExpenses()
    }
  })

  const openTravelExpensesMutation = useMutation({
    mutationFn: () => openTravelExpenses?.(id) ?? Promise.resolve(),
    onError: showAxiosError,
    onSuccess: () => {
      toast.success(t('travel.expenses.submission.success'))
      onOpen?.()
    }
  })

  function mapFieldToLabel(field: keyof TravelExpenseInput) {
    switch (field) {
      case 'date':
        return t('staff.personal.space.my.journeys.details.expenses.table.header.date')
      case 'details':
        return t('staff.personal.space.my.journeys.details.expenses.table.header.details')
      case 'amount':
        return t('staff.personal.space.my.journeys.details.expenses.table.header.amount')
      case 'validatedAmount':
        return t('staff.personal.space.my.journeys.details.expenses.table.header.amount.lec')
      case 'document':
        return t('staff.personal.space.my.journeys.details.expenses.table.header.proof')
      default:
        return field
    }
  }

  const details: DetailedListDetail[] = [
    {
      text: t('staff.personal.space.my.journeys.details.expenses.note.details.1'),
      displayArrow: true
    },
    {
      text: t('staff.personal.space.my.journeys.details.expenses.note.details.2'),
      displayArrow: true
    },
    ...(isForeignStaff
      ? []
      : [
          {
            text: t('staff.personal.space.my.journeys.details.expenses.note.details.3'),
            displayArrow: true,
            variant: 'text-destructive' as const
          },
          {
            text: t('staff.personal.space.my.journeys.details.expenses.note.details.4'),
            variant: 'text-destructive' as const
          }
        ]),
    { text: t('staff.personal.space.my.journeys.details.expenses.note.details.5') }
  ]

  const tripData = useMemo(
    () => newTravelExpenses.filter((e) => e.type === TravelExpenseTypeEnum.TRIP),
    [newTravelExpenses]
  )
  const preparatoryData = useMemo(
    () => newTravelExpenses.filter((e) => e.type === TravelExpenseTypeEnum.PREPARATORY),
    [newTravelExpenses]
  )

  const userRole: RoleEnum = mode === 'admin' ? RoleEnum.ADMIN : RoleEnum.STAFF

  const checkValidation = (
    values: TravelExpenseInput[],
    m: 'submit' | 'save'
  ): {
    fields: (keyof TravelExpenseInput)[]
    result: boolean
  } => {
    // Reject any completely empty row (alternatively: filter them out instead)
    if (values.some(isEmptyRow)) return { fields: [], result: false }

    if (m === 'submit') {
      const fields: (keyof TravelExpenseInput)[] = []
      values.forEach((v) => {
        if (!v.date) fields.push('date')
        if (!v.details?.trim()) fields.push('details')
        if (mode === 'staff' && v.amount == null) fields.push('amount')
        if (mode === 'admin' && v.validatedAmount == null) fields.push('validatedAmount')
        if (!v.document) fields.push('document')
      })
      // On submit: everything required (incl. document)
      return { fields, result: fields.length === 0 }
    }

    // On save: partial rows allowed as long as they don't violate the range check above
    return { fields: [], result: true }
  }

  function onDataChange(values: TravelExpenseInput[], type: TravelExpenseTypeEnum) {
    setNewTravelExpenses((prev) => [...prev.filter((e) => e.type !== type), ...values])
  }

  function addNewTravelExpense(type: TravelExpenseTypeEnum) {
    setNewTravelExpenses((prev) => [
      ...prev,
      { id: nextTempId.current--, type, createdByUserRole: userRole, validated: false }
    ])
  }
  function addDocument(document: IFile, id: number) {
    setNewTravelExpenses((prev) => prev.map((e) => (e.id === id ? { ...e, document } : e)))
  }

  function deleteDocument(id: number) {
    setNewTravelExpenses((prev) =>
      prev.map((e) => (e.id === id ? { ...e, document: undefined } : e))
    )
  }

  function constructErrorMessage(fields: (keyof TravelExpenseInput)[]) {
    const errorMessage = t('staff.personal.space.my.journeys.details.expenses.validation.error')
    if (fields.length === 0) return errorMessage

    const fieldsLabels = fields.map(mapFieldToLabel).join(', ')
    return `${errorMessage}: ${fieldsLabels}`
  }

  const columns: EditableTableColumn<TravelExpenseInput>[] = [
    ...getTravelExpensesColumns(t, ['date', 'details', 'amount'], 'staff'),
    {
      header: t('staff.personal.space.my.journeys.details.expenses.table.header.proof'),
      accessorKey: 'proof',
      centerCell: true,
      cell: ({ row }) =>
        row.original.document ? (
          <InvoiceButton
            variant={userInvoiceColorMapping[userRole]}
            invoice={row.original.document}
            readOnly={!isEditable}
            onDelete={() => deleteDocument(row.original.id)}
          />
        ) : isEditable ? (
          <UploadInvoicesButton
            variant={userUploadInvoiceColorMapping[userRole]}
            types={[FileTypeEnum.PDF, FileTypeEnum.IMAGE]}
            onChange={(files) => {
              if (files.length > 0) addDocument(files[0], row.original.id)
            }}
          />
        ) : (
          '-'
        )
    },
    ...(isEditable
      ? [
          {
            header: ' ',
            accessorKey: 'id',
            centerCell: true,
            cell: ({ row }: { row: { original: TravelExpenseInput } }) => (
              <div
                className="flex justify-center mr-5 items-center border border-grey-light bg-white-hover rounded-full cursor-pointer hover:bg-secondary-light-hover"
                onClick={() =>
                  setNewTravelExpenses(newTravelExpenses.filter((e) => e.id !== row.original.id))
                }
              >
                <MinusIcon color={tailwindColors.black.DEFAULT} />
              </div>
            )
          }
        ]
      : [])
  ]

  return (
    <div className={'flex flex-col gap-4'}>
      <div className="flex flex-row gap-2 items-center mobile:flex-col mobile:items-start">
        <Typography variant="h3">
          {t('staff.personal.space.my.journeys.details.expenses.note')}
        </Typography>

        {travelExpensesStatus === TravelExpensesStatusEnum.RECEIVED && (
          <Badge
            className="mobile:order-last mobile:self-end"
            size="medium"
            // Maryline : Passer la pastille 'envoyé à LEC le [date]” en couleur (orange?), car pas du tout visible en gris. =>  variant "orange" added in Badge component
            variant="orange"
            text={t('staff.personal.space.my.journeys.details.expenses.sent.to.lec.at', {
              date: valueRender(travelExpensesSentAt, (d) => DateFormatter.DDMMYYYY(d))
            })}
          />
        )}

        {travelExpensesStatus === TravelExpensesStatusEnum.VALIDATED && (
          <Badge
            className="mobile:order-last mobile:self-end"
            size="medium"
            variant={centerStatusMapping[travelExpensesStatus].variant}
            text={t(centerStatusMapping[travelExpensesStatus].travelExpenseLabel)}
          />
        )}
      </div>

      <DetailedList
        icon={() => (
          <div className="w-7 h-7 bg-red-light rounded-sm flex items-center justify-center p-1">
            <ClipboardIcon color={tailwindColors.red.DEFAULT} />
          </div>
        )}
        headers={[t('staff.personal.space.my.journeys.details.expenses.note.details.title')]}
        details={details}
      />

      <div className="flex flex-col gap-1">
        <Typography variant="secondary-body" color={tailwindColors.grey.lighter}>
          {t('staff.personal.space.my.journeys.details.expenses.note.sentence.1')}
        </Typography>
        <Typography variant="secondary-body" color={tailwindColors.grey.lighter}>
          {t('staff.personal.space.my.journeys.details.expenses.note.sentence.2')}
        </Typography>
      </div>

      <Box style="border" className="rounded-lg">
        <div className="flex justify-end items-center">
          <TravelExpensesCurrencyInput
            readonly={!isEditable}
            currency={currency}
            onChange={(currency) => {
              setCurrency(currency)
              updateTravelExpensesCurrency(currency)
            }}
          />
        </div>
        <TravelExpensesTable
          title={t('staff.personal.space.my.journeys.details.expenses.preparatory.table.title')}
          addText={t(
            'staff.personal.space.my.journeys.details.expenses.preparatory.table.footer.add.label'
          )}
          columns={columns}
          data={preparatoryData}
          isEditable={isEditable}
          onDataChange={onDataChange}
          type={TravelExpenseTypeEnum.PREPARATORY}
          onAddNewTravelExpense={addNewTravelExpense}
        />

        <TravelExpensesTable
          title={t('staff.personal.space.my.journeys.details.expenses.trip.table.title')}
          addText={t(
            'staff.personal.space.my.journeys.details.expenses.trip.table.footer.add.label'
          )}
          data={tripData}
          columns={columns}
          isEditable={isEditable}
          onDataChange={onDataChange}
          type={TravelExpenseTypeEnum.TRIP}
          onAddNewTravelExpense={addNewTravelExpense}
        />

        <Box
          style="border"
          className="bg-white-active flex-row justify-between items-center mobile:flex-col mobile:items-start  "
        >
          <div className="flex items-center gap-2 mobile:flex-col mobile:items-start tab:items-start tab:flex-col">
            <Typography variant="text">
              {isForeignStaff
                ? t('staff.personal.space.my.journeys.details.expenses.total')
                : t('staff.personal.space.my.journeys.details.expenses.flat.fees', {
                    flatFees: NumberFormatter.formatCurrency(170)
                  })}
            </Typography>

            <Badge
              variant="danger"
              text={NumberFormatter.formatCurrency(
                newTravelExpenses.reduce((sum, e) => sum + (e.amount ?? 0), 0),
                currency,
                'code'
              )}
            />
          </div>

          {/**Maryline : Asked to place the badge also here => mettre cette pastille également en bas là où j’ai entouré en rose. */}
          {travelExpensesStatus === TravelExpensesStatusEnum.RECEIVED && (
            <Badge
              className="mobile:self-end"
              size="medium"
              variant="orange"
              text={t('staff.personal.space.my.journeys.details.expenses.sent.to.lec.at', {
                date: valueRender(travelExpensesSentAt, (d) => DateFormatter.DDMMYYYY(d))
              })}
            />
          )}
        </Box>
      </Box>

      {mode === 'staff' && travelExpensesStatus === TravelExpensesStatusEnum.PENDING && (
        <div className="flex flex-row gap-2 justify-end mobile:flex-col">
          <Button
            onClick={() => {
              const res = checkValidation(newTravelExpenses, 'save')

              if (!res.result) {
                toast.error(constructErrorMessage(res.fields))
                return
              }
              saveTravelExpenses.mutateAsync(newTravelExpenses)
            }}
            variant="ghost"
            loading={saveTravelExpenses.isPending}
            disabled={saveTravelExpenses.isPending || !newTravelExpenses?.length}
            label={t('staff.personal.space.my.journeys.details.expenses.save.and.continue')}
          />
          <Button
            variant="primary"
            label={t('staff.personal.space.my.journeys.details.expenses.validate.and.send.to.lec')}
            loading={sendTravelExpensesToLecMutation.isPending}
            disabled={sendTravelExpensesToLecMutation.isPending || !newTravelExpenses.length}
            onClick={async () => {
              const res = checkValidation(newTravelExpenses, 'submit')
              if (!res.result) {
                toast.error(constructErrorMessage(res.fields))
                return
              }
              await sendTravelExpensesToLecMutation.mutateAsync(newTravelExpenses)
              await onSuccess()
            }}
          />
        </div>
      )}

      {mode === 'admin' && travelExpensesStatus === TravelExpensesStatusEnum.RECEIVED && (
        <div className="flex flex-row gap-2 justify-end">
          <Button
            variant="ghost"
            label={t('travel.expenses.open.for.staff')}
            loading={openTravelExpensesMutation.isPending}
            disabled={openTravelExpensesMutation.isPending || !newTravelExpenses.length}
            onClick={async () => {
              await openTravelExpensesMutation.mutateAsync()
            }}
          />
          <Button
            onClick={() => {
              if (!checkValidation(newTravelExpenses, 'save')) {
                toast.error(t('travel.expenses.validation.error'))
                return
              }
              saveTravelExpenses.mutateAsync(newTravelExpenses)
            }}
            variant="ghost"
            loading={saveTravelExpenses.isPending}
            disabled={saveTravelExpenses.isPending || !newTravelExpenses?.length}
            label={t('travel.expenses.validation.save')}
          />
          <Button
            variant="primary"
            label={t('travel.expenses.validate')}
            loading={validateTravelExpensesMutation.isPending}
            disabled={validateTravelExpensesMutation.isPending || !newTravelExpenses.length}
            onClick={async () => {
              if (!checkValidation(newTravelExpenses, 'submit')) {
                toast.error(t('travel.expenses.validation.error'))
                return
              }
              await validateTravelExpensesMutation.mutateAsync(newTravelExpenses)
            }}
          />
        </div>
      )}
      {mode === 'admin' && travelExpensesStatus === TravelExpensesStatusEnum.VALIDATED && (
        <div className="flex flex-row gap-2 justify-end">
          <Button
            variant="primary"
            label={t('travel.expenses.open')}
            loading={sendTravelExpensesToLecMutation.isPending}
            disabled={sendTravelExpensesToLecMutation.isPending || !newTravelExpenses.length}
            onClick={async () => {
              await sendTravelExpensesToLecMutation.mutateAsync(newTravelExpenses)
              await onSuccess()
            }}
          />
        </div>
      )}
    </div>
  )
}

export default TravelExpenses
